import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, CheckCircle, CircleAlert, AlertTriangle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMonthEndStock } from "@/hooks/useMonthEndStock";
import { useLocation } from "wouter";
import type { Item } from "@/types/item";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useItems } from "@/hooks/useItems";
import { Checkbox } from "@/components/ui/checkbox";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const monthEndStockSchema = z.object({
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  selectedItems: z.array(z.string()).optional(),
});

type MonthEndStockFormValues = z.infer<typeof monthEndStockSchema>;

export const MonthEndStockAdd = () => {
  const { formatCost, format: formatDecimal } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const { recalculate, isRecalculating } = useMonthEndStock();
  const { items: allItems, isLoading: isLoadingItems } = useItems();

  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<MonthEndStockFormValues>({
    resolver: zodResolver(monthEndStockSchema),
    defaultValues: {
      startDate: (() => {
        const date = new Date();
        date.setDate(1); 
        return format(date, "yyyy-MM-dd");
      })(),
      endDate: format(new Date(), "yyyy-MM-dd"),
      selectedItems: [],
    },
  });

  const itemsData = (allItems as any)?.data || [];

  const filteredItems = itemsData.filter((item: Item) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.itemCode.toLowerCase().includes(search) ||
      item.description.toLowerCase().includes(search)
    );
  });

  const handleSelectItem = (item: Item, checked: boolean) => {
    if (checked) {
      setSelectedItems((prev) => [...prev, item]);
    } else {
      setSelectedItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredItems) {
      // Select all filtered stock items
      const stockItems = filteredItems.filter((item: Item) => item.stockType === "stock");
      setSelectedItems(stockItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleConfirmSelection = () => {
    setValue("selectedItems", selectedItems.map((item) => item.id));
    setIsItemModalOpen(false);
    setSearchTerm("");
  };

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
    setValue(
      "selectedItems",
      selectedItems.filter((item) => item.id !== itemId).map((item) => item.id)
    );
  };

  const onSubmit = async (data: MonthEndStockFormValues) => {
    try {
      const result = await recalculate({
        startDate: data.startDate,
        endDate: data.endDate,
        itemIds: selectedItems.length > 0 ? selectedItems.map((item) => item.id) : null,
      });

      toast({
        title: "Recalculation Complete",
        description: `Period ${result.data.data.voucherNumber} created successfully`,
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });

      // Navigate to the results page
      navigate(`/month-end-stock/view/${result.data.data.periodId}`);
    } catch (error: any) {
      toast({
        title: "Recalculation Failed",
        description: error.response?.data?.message || error.message || "Failed to recalculate",
        variant: "destructive",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
      });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">New Month End Stock Recalculation</h1>
        <Button variant="outline" onClick={() => navigate("/month-end-stock")}>
          Back to List
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Period Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Recalculation Period</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">
                    Start Date <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="date"
                    {...register("startDate")}
                    className="h-10"
                  />
                  {errors.startDate && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.startDate.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="endDate">
                    End Date (Always Today)
                  </Label>
                  <Input
                    type="date"
                    {...register("endDate")}
                    className="h-10 bg-slate-100 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    End date is automatically set to today's date
                  </p>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold text-yellow-900">Important</p>
                  <p className="text-sm text-yellow-800">
                    This will recalculate weighted averages for all transactions in the selected period.
                    Large date ranges may take several minutes to process.
                  </p>
                </div>
              </div>
            </div>

            {/* Item Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Item Selection</h3>
                  <p className="text-sm text-muted-foreground">
                    Leave empty to recalculate all items
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsItemModalOpen(true)}
                  className="shadow"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Select Items
                </Button>
              </div>

              {/* Selected Items Display */}
              {selectedItems.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Selected Items ({selectedItems.length})</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedItems([]);
                        setValue("selectedItems", []);
                      }}
                      className="text-red-600 h-8"
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                        <TableRow>
                          <TableHead>Item Code</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Current Qty</TableHead>
                          <TableHead>Current WA</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.itemCode}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{formatDecimal(item.quantity) || "0.00"}</TableCell>
                            <TableCell>{formatCost(item.weightedAverage) || "0.000"}</TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(item.id)}
                                className="h-8 w-8 p-0 text-red-600"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground border rounded-md bg-slate-50 dark:bg-slate-800">
                  <p className="font-medium">All Items Selected</p>
                  <p className="text-sm mt-1">
                    The recalculation will process all stock items in the system
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/month-end-stock")}
                className="w-36"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-36 h-10 text-base"
                disabled={isRecalculating}
              >
                {isRecalculating ? "Recalculating..." : "Recalculate"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Item Selection Modal */}
      <Dialog open={isItemModalOpen} onOpenChange={setIsItemModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Items for Recalculation</DialogTitle>
            <DialogDescription>
              Choose specific items to recalculate, or close without selecting to recalculate all items
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div>
              <Input
                type="text"
                placeholder="Search by item code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-10"
              />
            </div>

            {/* Select All */}
            <div className="flex items-center justify-between border-b pb-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={
                    filteredItems &&
                    filteredItems.filter((item: Item) => item.stockType === "stock").length > 0 &&
                    filteredItems
                      .filter((item: Item) => item.stockType === "stock")
                      .every((item: Item) => selectedItems.some((si) => si.id === item.id))
                  }
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="font-semibold cursor-pointer">
                  Select All Stock Items
                </Label>
              </div>
              <Badge variant="secondary">
                {selectedItems.length} selected
              </Badge>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {isLoadingItems ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading items...
                </div>
              ) : filteredItems && filteredItems.length > 0 ? (
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50 dark:bg-slate-800">
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">WA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item: Item) => {
                      const isSelected = selectedItems.some((si) => si.id === item.id);
                      const isStockItem = item.stockType === "stock";

                      return (
                        <TableRow
                          key={item.id}
                          className={!isStockItem ? "opacity-50" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                handleSelectItem(item, checked as boolean)
                              }
                              disabled={!isStockItem}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{item.itemCode}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>
                            <Badge variant={isStockItem ? "default" : "secondary"}>
                              {item.stockType}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatDecimal(item.quantity) || "0.00"}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCost(item.weightedAverage) || "0.000"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No items found
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsItemModalOpen(false);
                setSearchTerm("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSelection}
            >
              Confirm Selection ({selectedItems.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthEndStockAdd;
